import {
  Component,
  inject,
  input,
  OnChanges,
  SimpleChanges,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { catchError, map, of, startWith, Subject, switchMap, tap } from 'rxjs';
import {
  DocumentType,
  getDocumentTypeLabel,
} from 'src/app/_enums/document-type.enum';
import { fmtDate } from 'src/app/_helpers/utils';
import { DocumentService } from 'src/app/_services/document.service';
import { CommunicationAttachmentSummary } from 'src/app/_types/communication.types';
import { DocumentSummary } from 'src/app/_types/document.types';
import { ComboboxComponent } from 'src/app/shared/components/combobox/combobox.component';
import { DocumentUploadModalComponent } from 'src/app/shared/components/document-upload-modal/document-upload-modal.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';

/** How many documents one search answers; the picker has no paging. */
const SEARCH_LIMIT = 50;

/**
 * Second line of a document row: type, creation date and the affaire it
 * belongs to (display id, policy number), each only when known.
 */
export function documentOptionDetail(document: DocumentSummary): string {
  return [
    document.type ? getDocumentTypeLabel(document.type) : null,
    document.createdOn ? fmtDate(document.createdOn) : null,
    document.flowDisplayId,
    document.policyId,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * A mail's attachment as a picked row: the id the request needs and the name
 * the row shows, no detail line — the timeline row carries nothing more.
 */
export function documentFromAttachment(
  attachment: CommunicationAttachmentSummary
): DocumentSummary {
  return {
    id: attachment.documentId,
    docName: attachment.fileName,
    originalFileName: null,
    type: null,
    createdOn: '',
    isPublished: true,
    flowDisplayId: null,
    policyId: null,
  };
}

/**
 * "Joindre un document existant" — a multi-select over the customer's stored
 * documents, for the compose modals that take attachments.
 *
 * app-combobox is single-select, so it is wrapped: every pick is moved out of
 * its control into the host's list control and the input is emptied again,
 * ready for the next one. The list renders beneath the field the way
 * app-file-input renders its files, with the same remove affordance.
 *
 * The search is document-store's, scoped to the account holder and narrowed
 * to the insured person (whose household's own papers answer too), with the
 * unpublished documents `DocumentService` asks for by default — invoices,
 * reminders and signed offers are stored that way, and are exactly what an
 * agent attaches.
 *
 * `allowUpload` adds the typed comboboxes' "+ Ajouter un document" row: the
 * upload modal stores a new document for the same holder and member, and the
 * next search picks it into the list (see `DocumentComboboxBase`).
 */
@Component({
  selector: 'app-document-picker',
  standalone: true,
  imports: [ComboboxComponent, DocumentUploadModalComponent, IconsModule],
  styleUrls: ['./document-option.css'],
  // The option styles must reach the overlay panel app-combobox opens.
  encapsulation: ViewEncapsulation.None,
  host: { class: 'document-picker' },
  template: `
    <div class="form-group mb-8">
      <label>{{ label() }}</label>
      <app-combobox
        [control]="picked"
        [items]="options"
        [loading]="loading"
        [displayValue]="optionName"
        [itemTemplate]="optionTpl"
        placeholder="Rechercher un document"
        i18n-placeholder
        [ariaLabel]="label()"
        loadingText="Chargement des documents…"
        i18n-loadingText
        emptyText="Aucun document trouvé"
        i18n-emptyText
        [errorText]="failed ? errorText : ''"
        [actionLabel]="canUpload ? uploadLabel : ''"
        (actionSelected)="uploadModal()?.openModal()"
        (searchChange)="search($event)"
      />

      @if (control().value.length) {
        <div class="mt-16">
          @for (document of control().value; track document.id) {
            <div class="uploaded-file-container" [class.mt-8]="!$first">
              <div class="uploaded-file-element">
                <app-file-icon
                  height="20px"
                  width="20px"
                  color="var(--gray-400)"
                />
                <div class="file-name">
                  <span>{{ document.docName }}</span>
                  <span>{{ optionDetail(document) }}</span>
                </div>
              </div>
              <app-trash-icon
                class="clickable"
                height="24px"
                width="24px"
                color="var(--gray-400)"
                (click)="remove(document.id)"
                (keydown.enter)="remove(document.id)"
                (keydown.space)="remove(document.id)"
                tabindex="0"
                role="button"
              />
            </div>
          }
        </div>
      }
    </div>

    @if (canUpload) {
      <app-document-upload-modal
        [userId]="userId()"
        [memberId]="familyMemberId()"
        [type]="DocumentType.OTHER"
        [hideCustomerFields]="true"
        (documentUploaded)="onUploaded($event)"
      />
    }

    <ng-template #optionTpl let-document>
      <span class="document-option">
        <span class="document-option__name">{{ document.docName }}</span>
        @if (optionDetail(document)) {
          <span class="document-option__detail">
            {{ optionDetail(document) }}
          </span>
        }
      </span>
    </ng-template>
  `,
})
export class DocumentPickerComponent implements OnChanges {
  private readonly documentService = inject(DocumentService);

  /** Receives the picked documents, in picking order. */
  readonly control = input.required<FormControl<DocumentSummary[]>>();

  /** The account holder; the search answers for one holder only. */
  readonly userId = input('');

  /** The insured person the exchange is about; empty means the whole household. */
  readonly familyMemberId = input('');

  readonly label = input($localize`Joindre un document existant`);

  /** Offers to store a new document, for a host without its own file input. */
  readonly allowUpload = input(false);

  /** What app-combobox writes to; emptied again as soon as a pick lands. */
  protected readonly picked = new FormControl<DocumentSummary | null>(null);

  private readonly combobox = viewChild.required(ComboboxComponent);

  protected readonly uploadModal = viewChild(DocumentUploadModalComponent);

  protected readonly uploadLabel = $localize`+ Ajouter un document`;

  protected readonly DocumentType = DocumentType;

  /** An upload the next search answer should pick; cleared by that answer. */
  private pendingUploadId: number | null = null;

  /** The last search's answer, minus the documents already picked. */
  protected options: DocumentSummary[] = [];

  private results: DocumentSummary[] = [];

  protected loading = false;

  protected failed = false;

  protected readonly errorText = $localize`Impossible de charger les documents`;

  /** Queries funnel through one stream so a slow earlier search never overwrites a later one. */
  private readonly query$ = new Subject<string>();

  constructor() {
    this.query$
      .pipe(
        tap(() => {
          this.loading = true;
          this.failed = false;
        }),
        switchMap((query) =>
          this.documentService
            .searchDocuments({
              userId: this.userId(),
              memberId: this.familyMemberId() || undefined,
              query: query.trim() || undefined,
              limit: SEARCH_LIMIT,
            })
            .pipe(
              map((res) => ({
                documents: res.data?.documents ?? [],
                failed: false,
              })),
              catchError(() =>
                of({ documents: [] as DocumentSummary[], failed: true })
              )
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe(({ documents, failed }) => {
        this.results = documents;
        this.failed = failed;
        this.loading = false;
        const uploaded = documents.find((d) => d.id === this.pendingUploadId);
        this.pendingUploadId = null;
        // Absent when the search does not surface it: nothing is picked then.
        if (uploaded) this.add(uploaded);
        this.refreshOptions();
      });

    // Follows the control it is given, and every value that lands in it — a
    // form reset empties the list, and the options must offer those again.
    toObservable(this.control)
      .pipe(
        switchMap((control) =>
          control.valueChanges.pipe(startWith(control.value))
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => this.refreshOptions());

    this.picked.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((document) => {
        if (!document) return;
        this.add(document);
        this.picked.setValue(null, { emitEvent: false });
        // Emptied on the combobox itself rather than through a bound signal:
        // when the agent picked without typing, a parent signal would go
        // '' -> '' and the binding would never reach the input, which would
        // keep the document's name and search for it 300 ms later.
        this.combobox().searchValue.set('');
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.userId()) return;
    if (changes['userId'] || changes['familyMemberId']) {
      this.query$.next('');
    }
  }

  /** The upload form needs the owner: hidden customer fields require both ids. */
  protected get canUpload(): boolean {
    return this.allowUpload() && !!this.userId() && !!this.familyMemberId();
  }

  /** The recent documents carry the upload; pick it once they answer. */
  protected onUploaded(documentId: number): void {
    this.pendingUploadId = documentId;
    // The field must not keep filtering by a text the list no longer answers.
    this.combobox().searchValue.set('');
    this.query$.next('');
  }

  /** Debounced by app-combobox, so this is already the settled query. */
  protected search(query: string): void {
    if (!this.userId()) return;
    this.query$.next(query);
  }

  protected remove(id: number): void {
    const control = this.control();
    control.setValue(control.value.filter((document) => document.id !== id));
    control.markAsTouched();
  }

  private add(document: DocumentSummary): void {
    const control = this.control();
    if (!control.value.some((picked) => picked.id === document.id)) {
      control.setValue([...control.value, document]);
    }
    control.markAsTouched();
  }

  private refreshOptions(): void {
    const pickedIds = new Set(this.control().value.map((d) => d.id));
    this.options = this.results.filter((d) => !pickedIds.has(d.id));
  }

  protected readonly optionName = (document: DocumentSummary): string =>
    document.docName;

  protected readonly optionDetail = documentOptionDetail;
}
